import type { Distribution } from "../../types";
import type { AppTheme } from "../../useAppTheme";

type ProgessData = {
	x: string;
	y: number;
};

export const masteryLevels = [
	{
		id: "basic",
		darkColor: "rgba(212, 84, 84, 1)",
		lightColor: "rgba(191, 0, 0, 1)",
		sortOrder: 1,
		icon: "speedometer-slow",
		range: {
			type: "formative",
			score: [0, 39],
			points: [0, 6],
		},
		alias: {
			title: "Basic",
		},
		level: "low",
	},
	{
		id: "lowIntermediate",
		darkColor: "rgba(181, 77, 33, 1)",
		lightColor: "rgba(255, 143, 96, 1)",
		sortOrder: 2,
		icon: "speedometer-medium",
		range: {
			type: "formative",
			score: [40, 59],
			points: [7, 12],
		},
		alias: {
			title: "Low-Intermediate",
		},
		level: "low",
	},
	{
		id: "highIntermediate",
		darkColor: "#77851C",
		lightColor: "#6A780E",
		sortOrder: 3,
		icon: "speedometer",
		range: {
			type: "formative",
			score: [60, 79],
			points: [13, 16],
		},
		alias: {
			title: "High-Intermediate",
		},
		level: "low",
	},
	{
		id: "advanced",
		darkColor: "rgba(82, 167, 141, 1)",
		lightColor: "rgba(0, 129, 89, 1)",
		sortOrder: 4,
		icon: "speedometer",
		range: {
			type: "formative",
			score: [80, 100],
			points: [17, 30],
		},
		alias: {
			title: "Advanced",
		},
		level: "high",
	},
];

export const sortedMasteryLevels = [...masteryLevels].sort(
	(a, b) => a.sortOrder - b.sortOrder,
);

const getMasteryProgessData = (dist: Distribution): ProgessData[] => {
	const distMap = Object.keys(dist).reduce<Record<string, number>>(
		(acc, curr) => {
			acc[curr] = dist[curr as keyof Distribution];
			return acc;
		},
		{},
	);

	return [
		...sortedMasteryLevels.map((level) => ({
			x: level.id,
			y: distMap[level.id] || 0,
		})),
		{ x: "none", y: distMap.none || 0 },
	];
};
const getProgressRingData = (
	distribution: Distribution | number[],
	selected?: boolean,
) => {
	if (!Array.isArray(distribution)) {
		return getMasteryProgessData(distribution);
	}
	if (selected) {
		return [{ x: "select", y: 1 }];
	}
	return [
		{ x: "select", y: distribution[0] },
		{ x: "select", y: distribution[1] },
	];
};

export const getProgressColorScale = (score: number, theme: AppTheme) => {
	const { none: empty } = theme.colors.progress;

	if (score <= 39) {
		const basic = sortedMasteryLevels.find((level) => level.id === "basic");
		return [theme.dark ? basic?.darkColor! : basic?.lightColor!, empty];
	}
	if (score <= 59) {
		const lowIntermediate = sortedMasteryLevels.find(
			(level) => level.id === "lowIntermediate",
		);
		return [
			theme.dark ? lowIntermediate?.darkColor! : lowIntermediate?.lightColor!,
			empty,
		];
	}
	if (score <= 79) {
		const highIntermediate = sortedMasteryLevels.find(
			(level) => level.id === "highIntermediate",
		);
		return [
			theme.dark ? highIntermediate?.darkColor! : highIntermediate?.lightColor!,
			empty,
		];
	}
	const advanced = sortedMasteryLevels.find((level) => level.id === "advanced");
	return [theme.dark ? advanced?.darkColor! : advanced?.lightColor!, empty];
};

const getColorScale = (
	theme: AppTheme,
	selected?: boolean,
	variant?: "progress" | "mastery",
	score?: number,
) => {
	const {
		colors: { progress, secondary },
	} = theme;
	const { none: empty } = progress;
	if (selected) {
		return [secondary];
	}
	if (variant === "progress") {
		return getProgressColorScale(score || 0, theme);
	}
	return [...sortedMasteryLevels.map((level) => level.lightColor), empty];
};
export { getProgressRingData, getColorScale };
